import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { RetryImage } from '@/components/ui/RetryImage';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useBlogMediaImages, flattenBlogMediaImages } from '@/hooks/useBlog';

/**
 * Single-select grid of the team's media-library images (Canva imports +
 * blog images across the team's posts — `GET /api/blog/media-images`), used
 * by `FeaturedImagePicker`'s "From library" affordance. Visually modeled on
 * `MediaPicker`'s grid, but talks to a DIFFERENT data source (the `media`
 * table, not `post_media`) — never reuse `MediaPicker`/`useMediaLibraryInfinite`
 * here.
 */
export interface BlogFeaturedImageLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMediaId?: number;
  onSelect: (image: { id: number; url: string }) => void;
}

export const BlogFeaturedImageLibraryModal = ({
  isOpen,
  onClose,
  selectedMediaId,
  onSelect,
}: BlogFeaturedImageLibraryModalProps) => {
  const { t } = useTranslation('common');
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useBlogMediaImages();

  const images = flattenBlogMediaImages(data);
  const isEmpty = !isLoading && images.length === 0;

  const handleSelect = (image: { id: number; url: string }) => {
    onSelect(image);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={onClose}
      title={t('blog_featured_library_title', { defaultValue: 'Choose a featured image' })}
      size="lg"
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-lg bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse"
                style={{ animationDelay: `${i * 50}ms` }}
              />
            ))}
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Icon name={ICONS.IMAGE} size="lg" color="text-gray-400" ariaHidden />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              {t('blog_featured_library_empty', { defaultValue: 'No images in your library yet' })}
            </h3>
            <p className="text-xs text-gray-500 max-w-xs">
              {t('blog_featured_library_empty_hint', { defaultValue: 'Upload one, or import from Canva.' })}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
            {images.map((image) => {
              const isSelected = selectedMediaId === image.id;
              return (
                <button
                  key={image.id}
                  type="button"
                  onClick={() => handleSelect({ id: image.id, url: image.url })}
                  className={cn(
                    'relative aspect-square rounded-lg overflow-hidden group',
                    'shadow-sm hover:shadow-md transition-shadow duration-200',
                    isSelected ? 'ring-2 ring-primary-500 ring-offset-2' : 'ring-1 ring-gray-100'
                  )}
                >
                  <RetryImage src={image.url} alt="" className="w-full h-full object-cover" />
                  {isSelected && (
                    <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                      <Icon name={ICONS.CHECK} size="xs" color="text-white" ariaHidden />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {hasNextPage && (
          <div className="pt-2 flex justify-center">
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {isFetchingNextPage ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  {t('blog_featured_load_more', { defaultValue: 'Load more' })}
                </span>
              ) : (
                t('blog_featured_load_more', { defaultValue: 'Load more' })
              )}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BlogFeaturedImageLibraryModal;
